import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  multiFactor,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  sendEmailVerification,
  EmailAuthProvider,
  getMultiFactorResolver,
  TotpMultiFactorGenerator,
  reauthenticateWithCredential,
} from "firebase/auth";
import { useAuth } from "../context/AuthContext";
import { auth } from "../firebaseConfig";
import { notify } from "./NotificationsPortal";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import TwoFATotpCard from "./TwoFATotpCard";
import { ShieldCheck, Smartphone, MessageSquare } from "lucide-react";
import ModalSms from "./ModalSms";
import { doc, onSnapshot, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebaseConfig";
import OtpVerifyModal from "./OtpVerifyModal";
import ConfirmIdentityModal from "./ConfirmIdentityModal";
import TotpVerifyModal from "./TotpVerifyModal";

export default function TwoFASmsCard({ onStatus }) {
  const { user } = useAuth();
  const [factors, setFactors] = useState([]);
  const [sending, setSending] = useState(false);
  const [loadingFactors, setLoadingFactors] = useState(true);
  const [method, setMethod] = useState("totp");
  const [smsModalOpen, setSmsModalOpen] = useState(false);
  const [phoneDisplay, setPhoneDisplay] = useState("");
  const [phoneE164, setPhoneE164] = useState("");
  const [otpOpen, setOtpOpen] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const otpResolverRef = useRef(null);
  const resendRef = useRef(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmError, setConfirmError] = useState("");
  const confirmResolverRef = useRef(null);
  const [totpOpen, setTotpOpen] = useState(false);
  const [totpError, setTotpError] = useState("");
  const [totpLoading, setTotpLoading] = useState(false);
  const totpResolverRef = useRef(null);

  const askTotpCode = useCallback(() => {
    setTotpOpen(true);
    setTotpError("");
    return new Promise((resolve) => {
      totpResolverRef.current = resolve;
    });
  }, []);

  const normalizePhoneToE164 = (raw = "") => {
    // Convierte "+52 33 3130 8335" -> "+523331308335"
    const cleaned = raw.replace(/[^\d+]/g, ""); // deja + y dígitos
    if (!cleaned.startsWith("+")) return "";
    return cleaned;
  };

  const formatPhoneDisplay = (raw = "") => {
    // Quita todo lo que no sea número o el símbolo +
    const cleaned = raw.replace(/[^\d+]/g, "");

    // Si no tiene el formato esperado, lo devuelve tal cual
    if (!cleaned.startsWith("+52")) return cleaned;

    // Aplica el formato: +52 33 3130 8335
    // Esto separa: Prefijo(3) - Espacio - Lada(2) - Espacio - Primeros(4) - Espacio - Resto
    const match = cleaned.match(/^(\+52)(\d{2})(\d{4})(\d{4})$/);
    if (match) {
      return `${match[1]} ${match[2]} ${match[3]} ${match[4]}`;
    }

    return cleaned;
  };

  const askOtpCode = useCallback(() => {
    setOtpOpen(true);
    return new Promise((resolve) => {
      otpResolverRef.current = resolve;
    });
  }, []);

  const recaptchaRef = useRef(null);

  const isEnrolled = (factors?.length || 0) > 0;

  const loadFactors = useCallback(async () => {
    if (!user) return;
    setLoadingFactors(true);
    await user.reload();

    const list = multiFactor(user).enrolledFactors || [];
    setFactors(list);

    // ✅ Sync Firestore con estado REAL de Firebase Auth
    try {
      const mfaRef = doc(db, "users", user.uid, "security", "mfa");

      if (!list.length) {
        // no tiene MFA → marcar desactivado
        await setDoc(
          mfaRef,
          {
            enabled: false,
            method: null,
            label: "",
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        );
      } else {
        const f = list[0];
        const isSms = f.factorId === "phone";
        const isTotp = f.factorId === "totp";

        await setDoc(
          mfaRef,
          {
            enabled: true,
            method: isSms ? "sms" : isTotp ? "totp" : "unknown",
            label: f.displayName || "",
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        );
      }
    } catch (e) {
      // si se cae por permisos momentáneos, no rompas la UI
      if (e?.code !== "permission-denied") console.warn("[MFA sync]", e);
    }

    // ✅ si ya tiene MFA, forzamos el radio seleccionado a lo que ya está activo
    if (list.length) {
      const factor = list[0];
      setMethod(factor?.factorId === "phone" ? "sms" : "totp");
    }

    setLoadingFactors(false);
  }, [user]);

  useEffect(() => {
    if (!user?.uid) return;

    const userRef = doc(db, "users", user.uid); // ✅ NUEVO MODELO
    const unsub = onSnapshot(
      userRef,
      (snap) => {
        if (!snap.exists()) {
          setPhoneDisplay("");
          setPhoneE164("");
          return;
        }

        // ✅ usa tu campo real del profile en users/{uid}
        // si tú guardas "telefono", déjalo así.
        // si guardas "phone", cámbialo a snap.data()?.phone
        const tel = (snap.data()?.telefono || "").trim();

        setPhoneDisplay(formatPhoneDisplay(tel));
        setPhoneE164(normalizePhoneToE164(tel));
      },
      (err) => {
        // evita spam cuando cambie auth / permisos momentáneos
        if (err?.code === "permission-denied") return;
        console.error("[TwoFASmsCard user phone]", err);
      },
    );

    return () => unsub();
  }, [user?.uid]);

  useEffect(() => {
    loadFactors();
    return () => {
      if (recaptchaRef.current) {
        try {
          recaptchaRef.current.clear();
        } catch (e) {}
        recaptchaRef.current = null;
      }
      // Limpieza física del div para que no queden iframes huérfanos de Google
      const container = document.getElementById("recaptcha-enroll");
      if (container) container.innerHTML = "";
    };
  }, [loadFactors]);

  const RECAPTCHA_DEBUG_VISIBLE = false;

  // BUSCA ESTA FUNCIÓN Y AJÚSTALA ASÍ:
  const ensureRecaptcha = useCallback(async () => {
    if (recaptchaRef.current) return recaptchaRef.current;

    // Validación de seguridad:
    const container = document.getElementById("recaptcha-enroll");
    if (!container) {
      console.warn("Contenedor reCAPTCHA no encontrado, reintentando...");
      // Esto evita que Firebase truene si el DOM no está listo
      return null;
    }

    const verifier = new RecaptchaVerifier(auth, "recaptcha-enroll", {
      size: RECAPTCHA_DEBUG_VISIBLE ? "normal" : "invisible",
      callback: (token) => console.log("[reCAPTCHA] token generado"),
      "expired-callback": () => console.warn("[reCAPTCHA] expirado"),
      "error-callback": (err) => console.error("[reCAPTCHA] error:", err),
    });

    recaptchaRef.current = verifier;
    await verifier.render();
    return verifier;
  }, [RECAPTCHA_DEBUG_VISIBLE]);

  const sendSms = useCallback(async () => {
    try {
      if (!user) return notify("error", "No hay sesión activa.");

      const e164 = (phoneE164 || "").trim();
      if (!/^\+\d{8,15}$/.test(e164)) {
        return notify(
          "error",
          "No tienes un teléfono válido en tu perfil. Guarda tu teléfono en Configuración para poder activar SMS.",
        );
      }

      await user.reload();

      if (!user.emailVerified) {
        try {
          await sendEmailVerification(user);
        } catch {}
        return notify(
          "warning",
          "Primero verifica tu correo. Te envié un email 📩",
        );
      }

      setSending(true);

      const session = await multiFactor(user).getSession();
      const verifier = await ensureRecaptcha();
      const provider = new PhoneAuthProvider(auth);

      // 1) Enviar SMS
      let verifyId = await provider.verifyPhoneNumber(
        { phoneNumber: e164, session },
        verifier,
      );

      // 2) Reenviar código
      resendRef.current = async () => {
        const newId = await provider.verifyPhoneNumber(
          { phoneNumber: e164, session },
          verifier,
        );
        verifyId = newId;
        setOtpError(""); // Limpiamos errores previos al reenviar
        notify("success", "Código reenviado ✅");
      };

      // 3) Ciclo de verificación
      let done = false;
      while (!done) {
        setOtpOpen(true);

        const code = await askOtpCode();

        if (!code) {
          notify("info", "Operación cancelada.");
          setOtpError("");
          return;
        }

        try {
          setOtpLoading(true);
          const cred = PhoneAuthProvider.credential(verifyId, code);
          const assertion = PhoneMultiFactorGenerator.assertion(cred);

          await multiFactor(user).enroll(assertion, "SMS");

          await setDoc(
            doc(db, "users", user.uid, "security", "mfa"),
            {
              enabled: true,
              method: "sms",
              phoneE164: (phoneE164 || "").trim(),
              phoneDisplay: (phoneDisplay || "").trim(),
              updatedAt: serverTimestamp(),
            },
            { merge: true },
          );

          // ✅ EXITO
          setOtpOpen(false);
          setOtpLoading(false);
          setOtpError("");

          await Swal.fire({
            icon: "success",
            title: "¡Activada con SMS!",
            text: "La autenticación de dos factores por SMS ha sido activada exitosamente.",
            confirmButtonText: "Aceptar",
            confirmButtonColor: "#16a34a",
            allowOutsideClick: false,
          });

          await loadFactors();
          notify("success", "2FA por SMS activado correctamente ✅");
          done = true;
        } catch (err) {
          setOtpLoading(false);
          console.error("Error en validación:", err.code);

          // TRADUCCIONES Y LÓGICA DE PERSISTENCIA
          if (err?.code === "auth/invalid-verification-code") {
            const msg = "Código incorrecto";
            notify("error", msg);
            setOtpError(msg);
            // El ciclo continúa, el modal se queda abierto
          } else if (err?.code === "auth/code-expired") {
            const msg = "El código ha expirado 🕒. Solicita uno nuevo.";
            notify("error", msg);
            setOtpError(msg);
            // El ciclo continúa, el usuario puede darle a "Reenviar" sin salir del modal
          } else {
            // Errores críticos que sí requieren cerrar (ej. sesión de Firebase expirada totalmente)
            setOtpOpen(false);
            notify("error", err?.message || "No se pudo verificar el código.");
            return;
          }
        }
      }
    } catch (e) {
      console.error("sendSms error:", e?.code);
      const map = {
        "auth/invalid-phone-number": "Número inválido.",
        "auth/too-many-requests": "Demasiados intentos. Intenta más tarde.",
        "auth/code-expired": "El código ha expirado.",
      };
      notify("error", map[e?.code] || e?.message || "Error al enviar SMS.");
    } finally {
      setSending(false);
    }
  }, [user, phoneE164, ensureRecaptcha, loadFactors, askOtpCode, phoneDisplay]);

  const showDisabledSuccess = useCallback(async () => {
    await Swal.fire({
      icon: "success",
      title: "Desactivada",
      text: "La autenticación de dos factores ha sido desactivada.",
      confirmButtonText: "Aceptar",
      confirmButtonColor: "#16a34a", // verde tipo tu screenshot
      allowOutsideClick: false,
      allowEscapeKey: false,
    });
  }, []);

  const removeFactor = useCallback(
    async (factorUid) => {
      try {
        if (!user?.email) {
          notify("error", "No hay sesión activa.");
          return;
        }

        // ✅ Abrimos el modal SOLO UNA VEZ antes del loop
        setConfirmError("");
        setConfirmLoading(false);
        setConfirmOpen(true);

        // helper: esperar password del modal
        const waitPassword = () =>
          new Promise((resolve) => {
            confirmResolverRef.current = resolve;
          });

        // 🔁 Loop: sigue pidiendo contraseña hasta que sea correcta o cancelen
        while (true) {
          const password = await waitPassword();

          // cancelado
          if (!password) {
            setConfirmOpen(false);
            notify("info", "Operación cancelada.");
            return;
          }

          try {
            setConfirmLoading(true);
            await user.reload();

            const cred = EmailAuthProvider.credential(user.email, password);
            await reauthenticateWithCredential(user, cred);

            // ✅ contraseña OK → cerramos y salimos del loop
            setConfirmOpen(false);
            setConfirmLoading(false);
            setConfirmError("");
            break;
          } catch (e) {
            const isWrongPassword =
              e?.code === "auth/wrong-password" ||
              e?.code === "auth/invalid-credential" ||
              e?.code === "auth/invalid-login-credentials";

            if (isWrongPassword) {
              const msg = "Contraseña incorrecta";
              setConfirmLoading(false);

              // ✅ se queda visible abajo en el modal
              setConfirmError(msg);

              // ✅ y también toast
              notify("error", msg);

              // ✅ seguimos esperando SIN reabrir el modal (si lo reabres, borras el error)
              continue;
            }

            throw e;
          }
        }

        // 🟩 Ya reautenticado → intentamos eliminar factor
        await multiFactor(user).unenroll(factorUid);

        await setDoc(
          doc(db, "users", user.uid, "security", "mfa"),
          {
            enabled: false,
            method: null,
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        );

        await loadFactors();
        await showDisabledSuccess();
        notify("success", "2FA desactivado correctamente ✅");
      } catch (err) {
        // 🟨 MFA requerido (Firebase exige 2FA para acción sensible)
        if (err?.code === "auth/multi-factor-auth-required") {
          // Cierra modal de contraseña
          setConfirmOpen(false);
          setConfirmLoading(false);
          setConfirmError("");

          try {
            const resolver = getMultiFactorResolver(auth, err);
            if (!resolver)
              throw new Error("No se pudo inicializar el resolver MFA.");

            const totpHint = resolver.hints?.find((h) => h.factorId === "totp");
            const phoneHint = resolver.hints?.find(
              (h) => h.factorId === "phone",
            );

            // --- ESCENARIO APP (TOTP) ---
            if (totpHint) {
              // 🔁 Igual que SMS: pedimos código con modal propio
              while (true) {
                const otp = await askTotpCode();

                // cancelado
                if (!otp) {
                  setTotpOpen(false);
                  notify("info", "Operación cancelada.");
                  return;
                }

                try {
                  setTotpLoading(true);
                  setTotpError("");

                  const assertion = TotpMultiFactorGenerator.assertionForSignIn(
                    totpHint.uid,
                    otp,
                  );

                  await resolver.resolveSignIn(assertion);

                  // ✅ ya resolvió MFA
                  setTotpOpen(false);
                  setTotpLoading(false);

                  await multiFactor(auth.currentUser).unenroll(factorUid);

                  await setDoc(
                    doc(db, "users", user.uid, "security", "mfa"),
                    {
                      enabled: false,
                      method: null,
                      updatedAt: serverTimestamp(),
                    },
                    { merge: true },
                  );
                  await loadFactors();
                  await showDisabledSuccess();
                  notify("success", "2FA desactivado correctamente ✅");
                  return;
                } catch (e) {
                  setTotpLoading(false);

                  const isWrong =
                    e?.code === "auth/invalid-verification-code" ||
                    e?.code === "auth/invalid-verification-token";

                  if (isWrong) {
                    const msg = "Código incorrecto";
                    setTotpError(msg);
                    notify("error", msg);
                    // 🔁 sigue el loop: el modal se queda abierto y el usuario reintenta
                    continue;
                  }

                  // otros errores: cerramos modal y salimos
                  setTotpOpen(false);
                  notify(
                    "error",
                    e?.message || "No se pudo verificar el código.",
                  );
                  return;
                }
              }
            }

            // --- ESCENARIO SMS (OTP Modal) ---
            if (phoneHint) {
              const verifier = await ensureRecaptcha();
              if (!verifier) {
                notify("error", "No se pudo inicializar reCAPTCHA.");
                return;
              }

              const phoneAuthProvider = new PhoneAuthProvider(auth);

              // 1) Enviar SMS
              let verifyId = await phoneAuthProvider.verifyPhoneNumber(
                { multiFactorHint: phoneHint, session: resolver.session },
                verifier,
              );

              // 2) Reenvío
              resendRef.current = async () => {
                verifyId = await phoneAuthProvider.verifyPhoneNumber(
                  { multiFactorHint: phoneHint, session: resolver.session },
                  verifier,
                );
                setOtpError("");
                notify("success", "Código reenviado ✅");
              };

              // 3) Ciclo OTP
              const maxAttempts = 3;
              let attempts = 0;

              while (attempts < maxAttempts) {
                setOtpOpen(true);

                const code = await askOtpCode();

                if (!code) {
                  setOtpOpen(false);
                  notify("info", "Operación cancelada.");
                  return;
                }

                try {
                  setOtpLoading(true);
                  setOtpError("");

                  const phoneCred = PhoneAuthProvider.credential(
                    verifyId,
                    code,
                  );
                  const assertion =
                    PhoneMultiFactorGenerator.assertion(phoneCred);

                  await resolver.resolveSignIn(assertion);

                  // ✅ OTP OK
                  setOtpOpen(false);
                  setOtpLoading(false);

                  await multiFactor(auth.currentUser).unenroll(factorUid);

                  await setDoc(
                    doc(db, "users", user.uid, "security", "mfa"),
                    {
                      enabled: false,
                      method: null,
                      updatedAt: serverTimestamp(),
                    },
                    { merge: true },
                  );
                  await loadFactors();
                  await showDisabledSuccess();
                  notify("success", "2FA desactivado correctamente ✅");
                  return;
                } catch (verifyErr) {
                  setOtpLoading(false);

                  if (verifyErr?.code === "auth/invalid-verification-code") {
                    attempts++;
                    const msg = "Código incorrecto";
                    setOtpError(msg);
                    notify("error", msg);

                    if (attempts >= maxAttempts) {
                      setOtpOpen(false);
                      notify("error", "Demasiados intentos fallidos.");
                      return;
                    }
                  } else if (verifyErr?.code === "auth/code-expired") {
                    const msg = "El código ha expirado. Solicita uno nuevo.";
                    setOtpError(msg);
                    notify("error", msg);
                  } else {
                    throw verifyErr;
                  }
                }
              }

              return;
            }

            notify("error", "No se encontró un método MFA disponible.");
            return;
          } catch (mfaErr) {
            console.error("Error en flujo MFA:", mfaErr);
            setOtpOpen(false);
            notify(
              "error",
              mfaErr?.message || "No se pudo completar la verificación MFA.",
            );
            return;
          } finally {
            setOtpLoading(false);
          }
        }

        console.error("removeFactor error:", err);
        notify("error", err?.message || "No se pudo completar la acción.");
      } finally {
        setConfirmLoading(false);
        setOtpLoading(false);
      }
    },
    [
      user,
      loadFactors,
      showDisabledSuccess,
      ensureRecaptcha,
      askTotpCode,
      askOtpCode,
    ],
  );

  // ✅ si ya tiene factores, forzamos UI a mostrar “desactivar” (y bloqueamos activar)
  const firstFactor = factors?.[0];
  const enrolledType = firstFactor?.factorId; // "phone" | "totp"
  const enrolledLabel =
    enrolledType === "phone"
      ? `SMS ${firstFactor?.displayName ? `(${firstFactor.displayName})` : ""}`
      : enrolledType === "totp"
        ? `Aplicación de autenticación${
            firstFactor?.displayName ? ` (${firstFactor.displayName})` : ""
          }`
        : "2FA";

  useEffect(() => {
    onStatus?.({ isEnrolled, enrolledLabel });
  }, [onStatus, isEnrolled, enrolledLabel]);

  const handleDisable = useCallback(async () => {
    if (!firstFactor?.uid) return;

    const res = await Swal.fire({
      icon: "warning",
      title: "¿Desactivar autenticación de dos factores?",
      text: "Tu cuenta será menos segura sin la autenticación de dos factores.",
      showCancelButton: true,
      confirmButtonText: "Desactivar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      reverseButtons: false,
    });

    if (!res.isConfirmed) return;

    // ✅ ahora sí ejecuta tu flujo actual (pedir password, etc.)
    await removeFactor(firstFactor.uid);
  }, [firstFactor?.uid, removeFactor]);

  return (
    <div className="twofa">
      {/* pill arriba si está activado */}
      {isEnrolled ? (
        <span className="twofa-active-pill">
          <ShieldCheck size={16} />
          Activada
        </span>
      ) : null}

      {isEnrolled ? (
        <div className="twofa-active-view">
          <div className="twofa-method-box">
            <span>Método activo:</span>
            <b>{enrolledLabel}</b>
          </div>

          <div className="twofa-actions">
            <button
              type="button"
              className="security-btn-primary"
              onClick={handleDisable}
              disabled={loadingFactors || sending}
            >
              Desactivar
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="twofa-label">Método de autenticación</div>

          <div
            className="twofa-grid"
            role="radiogroup"
            aria-label="Método de autenticación"
          >
            <div
              role="radio"
              aria-checked={method === "totp"}
              tabIndex={0}
              className={`twofa-option ${method === "totp" ? "selected" : ""}`}
              onClick={() => setMethod("totp")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") setMethod("totp");
              }}
            >
              <div className="twofa-option-icon">
                <Smartphone size={18} className="twofa-lucide-icon" />
              </div>

              <div className="twofa-option-text">
                <div className="twofa-option-title">App de Autenticación</div>
                <div className="twofa-option-desc">
                  Google Authenticator, Authy
                </div>
              </div>
            </div>

            <div
              role="radio"
              aria-checked={method === "sms"}
              tabIndex={0}
              className={`twofa-option ${method === "sms" ? "selected" : ""}`}
              onClick={() => setMethod("sms")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") setMethod("sms");
              }}
            >
              <div className="twofa-option-icon">
                <MessageSquare size={18} className="twofa-lucide-icon" />
              </div>

              <div className="twofa-option-text">
                <div className="twofa-option-title">Mensaje de Texto (SMS)</div>
                <div className="twofa-option-desc">
                  Código de verificación por SMS
                </div>
              </div>
            </div>
          </div>

          <div className="twofa-actions">
            {method === "sms" ? (
              <button
                type="button"
                className="security-btn-primary twofa-activate-btn"
                onClick={() => setSmsModalOpen(true)}
                disabled={sending || loadingFactors}
              >
                Activar autenticación
              </button>
            ) : (
              <TwoFATotpCard onDone={loadFactors} />
            )}
          </div>
        </>
      )}

      <ModalSms
        isOpen={smsModalOpen}
        onClose={() => setSmsModalOpen(false)}
        phoneDisplay={phoneDisplay}
        loading={sending}
        onConfirmSend={async () => {
          setSmsModalOpen(false);
          await sendSms();
        }}
      />

      <OtpVerifyModal
        isOpen={otpOpen}
        phoneE164={phoneE164}
        cooldownSec={21}
        loading={otpLoading}
        errorText={otpError}
        onClose={() => {
          setOtpOpen(false);
          otpResolverRef.current?.(null);
          otpResolverRef.current = null;
          setOtpError("");
        }}
        onResend={async () => {
          await resendRef.current?.();
        }}
        onSubmitCode={(code) => {
          otpResolverRef.current?.(code);
          otpResolverRef.current = null;
        }}
      />

      <ConfirmIdentityModal
        isOpen={confirmOpen}
        loading={confirmLoading}
        errorText={confirmError}
        onClose={() => {
          setConfirmOpen(false);
          confirmResolverRef.current?.(null);
          confirmResolverRef.current = null;
        }}
        onConfirm={(pass) => {
          confirmResolverRef.current?.(pass);
          confirmResolverRef.current = null; // OK
        }}
        onChangePassword={() => {
          if (confirmError) setConfirmError("");
        }}
      />

      <TotpVerifyModal
        isOpen={totpOpen}
        loading={totpLoading}
        errorText={totpError}
        onClose={() => {
          setTotpOpen(false);
          totpResolverRef.current?.(null);
          totpResolverRef.current = null;
          setTotpError("");
        }}
        onSubmitCode={(code) => {
          totpResolverRef.current?.(code);
          totpResolverRef.current = null;
        }}
      />

      {/* reCAPTCHA */}
      <div
        id="recaptcha-enroll"
        style={{ position: "fixed", bottom: 12, right: 12, zIndex: 9999 }}
      />
    </div>
  );
}
